import React, { useState, useEffect, useContext } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Typography, Button, IconButton, Chip, TextField, InputAdornment,
    Grid, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select, MenuItem, Fab, CircularProgress, Alert,
    Switch, FormControlLabel, Tooltip
} from '@mui/material';
import {
    Search as SearchIcon, Add as AddIcon, Edit as EditIcon,
    Settings as SettingsIcon, CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon, Launch as LaunchIcon, Link as LinkIcon,
    Autorenew as AutorenewIcon, Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon, Delete as DeleteIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import axios from 'axios';
import { TokenContext } from '../Context/TokenContext';
import { UserContext } from '../Context/UserContext';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

const emptyForm = {
    client_id: '',
    client_secret: '',
    name: '',
    short_description: '',
    long_description: '',
    redirect_urls: '',
    allow_all_redirect_url: false,
    requires_consent: true,
    is_first_party: false,
    is_internal: false,
    isActive: false,
};

export default function ApplicationsPage() {
    const navigate = useNavigate();
    const { accessToken } = useContext(TokenContext);
    const { userInfo } = useContext(UserContext);
    const isSuperAdmin = userInfo?.is_super_admin === true;
    const userPermissions = userInfo?.permissions || [];
    const canManageApps = isSuperAdmin || userPermissions.includes('applications.manage');
    const canDeleteApps = isSuperAdmin || userPermissions.includes('applications.delete');

    const [applications, setApplications] = useState([]);
    const [loading, setLoading]           = useState(true);
    const [error, setError]               = useState(null);
    const [page, setPage]                 = useState(0);
    const [rowsPerPage, setRowsPerPage]   = useState(10);
    const [searchTerm, setSearchTerm]     = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [openDialog, setOpenDialog]     = useState(false);
    const [formData, setFormData]         = useState(emptyForm);
    const [saving, setSaving]             = useState(false);
    const [saveError, setSaveError]       = useState(null);
    const [showSecret, setShowSecret]     = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [editingApp, setEditingApp]     = useState(null);
    const [editForm, setEditForm]         = useState({});

    const authHeaders = { Authorization: `Bearer ${accessToken}` };

    // ── Load applications ──────────────────────────────────────────────────────
    useEffect(() => {
        if (!accessToken) return;
        fetchApplications();
    }, [accessToken]);

    const fetchApplications = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await axios.get(`${API_URL}/api/applications`, { headers: authHeaders });
            setApplications(res.data);
        } catch (e) {
            setError('Impossible de charger les applications');
        } finally {
            setLoading(false);
        }
    };

    // ── Filtering ──────────────────────────────────────────────────────────────
    const filtered = applications.filter(app => {
        const matchSearch = (app.name || '').toLowerCase().includes(searchTerm.toLowerCase())
            || (app.short_description || '').toLowerCase().includes(searchTerm.toLowerCase())
            || (app.client_id || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchStatus = statusFilter === 'all'
            || (statusFilter === 'active'   &&  app.isActive)
            || (statusFilter === 'inactive' && !app.isActive);
        return matchSearch && matchStatus;
    });

    // ── Stats ──────────────────────────────────────────────────────────────────
    const stats = {
        total:        applications.length,
        active:       applications.filter(a => a.isActive).length,
        inactive:     applications.filter(a => !a.isActive).length,
        firstParty:   applications.filter(a => a.is_first_party).length,
    };

    // ── Handlers ───────────────────────────────────────────────────────────────
    const handleToggleActive = async (app) => {
        try {
            await axios.put(
                `${API_URL}/api/applications/${app.client_id}`,
                { isActive: !app.isActive },
                { headers: authHeaders }
            );
            setApplications(prev =>
                prev.map(a => a.client_id === app.client_id ? { ...a, isActive: !a.isActive } : a)
            );
        } catch (e) {
            console.error('Error toggling app status:', e.message);
        }
    };

    const generateSecret = () => {
        const array = new Uint8Array(32);
        window.crypto.getRandomValues(array);
        const secret = Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
        setFormData(prev => ({ ...prev, client_secret: secret }));
        setShowSecret(true);
    };

    const handleOpenCreate = () => {
        setFormData(emptyForm);
        setSaveError(null);
        setShowSecret(false);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSaveError(null);
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            const payload = {
                ...formData,
                redirect_urls: formData.redirect_urls
                    ? formData.redirect_urls.split('\n').map(u => u.trim()).filter(Boolean)
                    : [],
            };
            const res = await axios.post(`${API_URL}/api/applications`, payload, { headers: authHeaders });
            setApplications(prev => [...prev, res.data]);
            handleCloseDialog();
        } catch (e) {
            setSaveError(e.response?.data?.message || e.message || 'Erreur lors de la création');
        } finally {
            setSaving(false);
        }
    };

    const handleOpenEdit = (app) => {
        setEditingApp(app);
        setEditForm({
            name: app.name || '',
            short_description: app.short_description || '',
            long_description: app.long_description || '',
            redirect_urls: (app.redirect_urls || []).join('\n'),
            allow_all_redirect_url: app.allow_all_redirect_url || false,
            requires_consent: app.requires_consent ?? true,
            is_first_party: app.is_first_party || false,
            is_internal: app.is_internal || false,
            isActive: app.isActive || false,
        });
        setSaveError(null);
        setOpenEditDialog(true);
    };

    const handleSaveEdit = async () => {
        setSaving(true); setSaveError(null);
        try {
            const payload = {
                ...editForm,
                redirect_urls: editForm.redirect_urls
                    ? editForm.redirect_urls.split('\n').map(u => u.trim()).filter(Boolean)
                    : [],
            };
            const res = await axios.put(
                `${API_URL}/api/applications/${editingApp.client_id}`,
                payload, { headers: authHeaders }
            );
            setApplications(prev => prev.map(a =>
                a.client_id === editingApp.client_id ? { ...a, ...res.data } : a
            ));
            setOpenEditDialog(false);
        } catch (e) {
            setSaveError(e.response?.data?.message || e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (app) => {
        if (!window.confirm(`Supprimer l'application "${app.name}" ?`)) return;
        try {
            await axios.delete(`${API_URL}/api/applications/${app.client_id}`, { headers: authHeaders });
            setApplications(prev => prev.filter(a => a.client_id !== app.client_id));
        } catch (e) {
            console.error('Error deleting application:', e.message);
        }
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Gestion des Applications
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Stats */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {[
                    { label: 'Total',         value: stats.total },
                    { label: 'Actives',        value: stats.active,      color: 'success.main' },
                    { label: 'Inactives',      value: stats.inactive,    color: 'error.main' },
                    { label: 'First-party',    value: stats.firstParty },
                ].map(s => (
                    <Grid item xs={12} sm={6} md={3} key={s.label}>
                        <Card>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>{s.label}</Typography>
                                <Typography variant="h4" sx={{ color: s.color || 'primary.main' }}>
                                    {s.value}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Search & filters */}
            <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
                <TextField
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                    sx={{ flexGrow: 1 }}
                />
                <FormControl sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                    <InputLabel>Statut</InputLabel>
                    <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} label="Statut">
                        <MenuItem value="all">Tous</MenuItem>
                        <MenuItem value="active">Actif</MenuItem>
                        <MenuItem value="inactive">Inactif</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Table */}
            <Paper variant="outlined" sx={{ overflowX: 'hidden', width: '100%' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <TableContainer>
                        <Table size="small" sx={{ minWidth: { xs: '100%', sm: '700px' } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Application</TableCell>
                                    <TableCell>Client ID</TableCell>
                                    <TableCell>Redirect URLs</TableCell>
                                    <TableCell>Statut</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <CircularProgress size={24} />
                                        </TableCell>
                                    </TableRow>
                                ) : filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(app => (
                                    <TableRow key={app.client_id} hover>
                                        <TableCell>
                                            <Typography variant="subtitle2" fontWeight="bold">{app.name}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {app.short_description || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontFamily="monospace" fontSize={12}>
                                                {app.client_id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {(app.redirect_urls || []).slice(0, 2).map((url, i) => (
                                                <Typography key={i} variant="body2" fontSize={11} color="text.secondary"
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <LinkIcon sx={{ fontSize: 12 }} />{url}
                                                </Typography>
                                            ))}
                                            {app.allow_all_redirect_url && (
                                                <Chip label="Toutes URLs" size="small" color="warning" />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={app.isActive ? 'Actif' : 'Inactif'}
                                                color={app.isActive ? 'success' : 'default'}
                                                size="small"
                                                icon={app.isActive ? <CheckCircleIcon /> : <CancelIcon />}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {app.is_internal && <Chip label="Interne" size="small" sx={{ bgcolor: '#1e3a5f', color: '#fff', mr: 0.5 }} />}
                                            {app.is_first_party && <Chip label="First-party" size="small" sx={{ bgcolor: 'primary.main', color: '#fff' }} />}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                onClick={() => navigate(`/applications/${app.client_id}`)}
                                                size="small" color="primary" title="Gérer"
                                            >
                                                <SettingsIcon />
                                            </IconButton>
                                            {canManageApps && (
                                                <IconButton
                                                    onClick={() => handleOpenEdit(app)}
                                                    size="small" title="Modifier"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            {canDeleteApps && (
                                                <>
                                                    <IconButton
                                                        onClick={() => handleToggleActive(app)}
                                                        size="small"
                                                        sx={{ color: app.isActive ? 'error.main' : 'success.main' }}
                                                        title={app.isActive ? 'Désactiver' : 'Activer'}
                                                    >
                                                        {app.isActive ? <CancelIcon /> : <CheckCircleIcon />}
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={() => handleDelete(app)}
                                                        size="small"
                                                        sx={{ color: 'error.main' }}
                                                        title="Supprimer"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {!loading && filtered.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography color="text.secondary">Aucune application trouvée</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filtered.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>

            {/* FAB */}
            {canManageApps && (
                <Fab
                    color="primary"
                    sx={{ position: 'fixed', bottom: 16, right: 16 }}
                    onClick={handleOpenCreate}
                >
                    <AddIcon />
                </Fab>
            )}

            {/* Edit dialog */}
            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Modifier l'application</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Nom *" value={editForm.name || ''}
                                onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Description courte" value={editForm.short_description || ''}
                                onChange={e => setEditForm({ ...editForm, short_description: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Description longue" multiline rows={3}
                                value={editForm.long_description || ''}
                                onChange={e => setEditForm({ ...editForm, long_description: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Redirect URLs (une par ligne)" multiline rows={3}
                                value={editForm.redirect_urls || ''}
                                onChange={e => setEditForm({ ...editForm, redirect_urls: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Switch checked={!!editForm.isActive}
                                    onChange={e => setEditForm({ ...editForm, isActive: e.target.checked })} />}
                                label="Active"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Switch checked={!!editForm.is_first_party}
                                    onChange={e => setEditForm({ ...editForm, is_first_party: e.target.checked })} />}
                                label="First-party"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Switch checked={!!editForm.is_internal}
                                    onChange={e => setEditForm({ ...editForm, is_internal: e.target.checked })} />}
                                label="Interne"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Switch checked={!!editForm.requires_consent}
                                    onChange={e => setEditForm({ ...editForm, requires_consent: e.target.checked })} />}
                                label="Requires consent (OAuth2)"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={!!editForm.allow_all_redirect_url}
                                    onChange={e => setEditForm({ ...editForm, allow_all_redirect_url: e.target.checked })} />}
                                label="Autoriser toutes les redirect URLs"
                            />
                        </Grid>
                        {saveError && <Grid item xs={12}><Alert severity="error">{saveError}</Alert></Grid>}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditDialog(false)}>Annuler</Button>
                    <Button onClick={handleSaveEdit} variant="contained" color="primary" disabled={saving}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : 'Enregistrer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Nouvelle application</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Client ID *" value={formData.client_id}
                                onChange={e => setFormData({ ...formData, client_id: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Client Secret *"
                                value={formData.client_secret}
                                type={showSecret ? 'text' : 'password'}
                                onChange={e => setFormData({ ...formData, client_secret: e.target.value })}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title={showSecret ? 'Masquer' : 'Afficher'}>
                                                <IconButton size="small" onClick={() => setShowSecret(v => !v)}>
                                                    {showSecret ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Générer un secret aléatoire sécurisé">
                                                <IconButton size="small" onClick={generateSecret} color="primary">
                                                    <AutorenewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Nom *" value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Description courte" value={formData.short_description}
                                onChange={e => setFormData({ ...formData, short_description: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Description longue" multiline rows={3}
                                value={formData.long_description}
                                onChange={e => setFormData({ ...formData, long_description: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Redirect URLs (une par ligne)" multiline rows={3}
                                value={formData.redirect_urls}
                                onChange={e => setFormData({ ...formData, redirect_urls: e.target.value })}
                                placeholder="http://localhost:3000/authentication/callback" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Switch checked={formData.isActive}
                                    onChange={e => setFormData({ ...formData, isActive: e.target.checked })} />}
                                label="Active"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Switch checked={formData.is_first_party}
                                    onChange={e => setFormData({ ...formData, is_first_party: e.target.checked })} />}
                                label="First-party"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Switch checked={formData.is_internal}
                                    onChange={e => setFormData({ ...formData, is_internal: e.target.checked })} />}
                                label="Application interne (accès CRUD données sensibles)"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={formData.allow_all_redirect_url}
                                    onChange={e => setFormData({ ...formData, allow_all_redirect_url: e.target.checked })} />}
                                label="Autoriser toutes les redirect URLs"
                            />
                        </Grid>
                        {saveError && <Grid item xs={12}><Alert severity="error">{saveError}</Alert></Grid>}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Annuler</Button>
                    <Button onClick={handleSave} variant="contained" color="primary" disabled={saving}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : 'Créer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
